import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { FaEye, FaSearch, FaFilter, FaFileInvoice, FaCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

function AllOrder() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        status: filters.status || undefined,
        search: filters.search || undefined,
        start_date: filters.fromDate ? filters.fromDate.toISOString().split("T")[0] : undefined,
        end_date: filters.toDate ? filters.toDate.toISOString().split("T")[0] : undefined,
      };

      const res = await axios.get(`${baseurl}getAllOrdersForAdmin`, { params });
      if (res.data.success) {
        setOrders(res.data.orders || []);
        setTotalCount(res.data.count || 0);
      }
    } catch (err) {
      console.error("Fetch orders error:", err);
      Swal.fire("Error", "Failed to fetch orders", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [filters.status, filters.fromDate, filters.toDate, page, limit]);

  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(1);
      fetchOrders();
    }, 600);
    return () => clearTimeout(delay);
  }, [filters.search]);

  // Helper for Status UI
  const getStatusBadge = (status) => {
    const map = {
      Delivered: "status-delivered",
      Cancelled: "status-cancelled",
      Shipped: "status-shipped",
      Confirmed: "status-confirmed",
      Pending: "status-pending",
    };
    return map[status] || "bg-secondary";
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light">
        <div className="main-content p-3 p-lg-4">
          
          {/* Header & Quick Stats */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div>
              <h4 className="fw-bold mb-1 text-dark">Order Ledger</h4>
              <p className="text-muted small mb-0">Total of <strong>{totalCount}</strong> transactions found in the system.</p>
            </div>
            <div className="d-flex gap-2">
                <div className="stat-pill bg-white border px-3 py-2 rounded-3 shadow-sm">
                    <small className="text-muted d-block lh-1">Revenue Stream</small>
                    <span className="fw-bold">₹{orders.reduce((acc, curr) => acc + (curr.total_amount || 0), 0).toLocaleString()}</span>
                </div>
                <div className="stat-pill bg-dark text-white px-3 py-2 rounded-3 shadow-sm">
                    <small className="text-white-50 d-block lh-1">Page Volume</small>
                    <span className="fw-bold">{orders.length} Orders</span>
                </div>
            </div>
          </div>

          {/* Compact Filter Bar */}
          <div className="card border-0 shadow-sm rounded-3 mb-3">
            <div className="card-body p-3">
              <div className="row g-2 align-items-center">
                <div className="col-md-4">
                  <div className="input-group input-group-sm border rounded-2 bg-light">
                    <span className="input-group-text bg-transparent border-0"><FaSearch className="text-muted" /></span>
                    <input
                      type="text"
                      className="form-control bg-transparent border-0"
                      placeholder="Search ID, Customer, or Vendor..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select form-select-sm border-0 bg-light"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <option value="">All Statuses</option>
                    <option value="Pending">Pending</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="Ready To Ship">Ready To Ship</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <div className="d-flex gap-2 align-items-center">
                    <Flatpickr
                      options={{ dateFormat: "Y-m-d" }}
                      placeholder="Start Date"
                      className="form-control form-control-sm border-0 bg-light text-center"
                      value={filters.fromDate}
                      onChange={(date) => setFilters({ ...filters, fromDate: date[0] })}
                    />
                    <span className="text-muted small">to</span>
                    <Flatpickr
                      options={{ dateFormat: "Y-m-d" }}
                      placeholder="End Date"
                      className="form-control form-control-sm border-0 bg-light text-center"
                      value={filters.toDate}
                      onChange={(date) => setFilters({ ...filters, toDate: date[0] })}
                    />
                  </div>
                </div>
                <div className="col-md-2 text-end">
                    <button className="btn btn-sm btn-outline-secondary w-100 border-0" onClick={() => setFilters({search: "", status: "", fromDate: "", toDate: ""})}>Reset Filters</button>
                </div>
              </div>
            </div>
          </div>

          {/* The Order Table */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table align-middle table-hover mb-0 order-table">
                <thead className="bg-white border-bottom">
                  <tr>
                    <th className="ps-4">#</th>
                    <th>Order Details</th>
                    <th>Customer Persona</th>
                    <th>Vendor Origin</th>
                    <th>Value</th>
                    <th>Status</th>
                    <th>Method</th>
                    <th className="text-end pe-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="8" className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                  ) : orders.length > 0 ? (
                    orders.map((o, i) => (
                      <tr key={o.order_id}>
                        <td className="ps-4 text-muted small">{(page - 1) * limit + i + 1}</td>
                        <td>
                          <div className="fw-bold text-primary mb-0 font-monospace" style={{ fontSize: '12px' }}>
                            #{o.order_uuid.substring(0, 8)}...
                          </div>
                          <div className="text-muted extra-small">
                            {new Date(o.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                        </td>
                        <td>
                          <div className="fw-bold small">{o.customer_name}</div>
                          <div className="text-muted extra-small">{o.customer_phone}</div>
                        </td>
                        <td>
                          {o.vendors?.slice(0, 1).map((v, idx) => (
                            <div key={idx} className="vendor-chip">
                               <FaCircle className="me-1 text-info" size={6} /> {v.business_name}
                            </div>
                          ))}
                          {o.vendors?.length > 1 && <span className="extra-small text-primary fw-bold">+{o.vendors.length - 1} more</span>}
                        </td>
                        <td>
                          <div className="fw-bold small text-dark">₹{o.total_amount?.toLocaleString()}</div>
                          <span className={`payment-pill ${o.payment_status === "Paid" ? "paid" : "unpaid"}`}>
                            {o.payment_status}
                          </span>
                        </td>
                        <td>
                          <span className={`status-tag ${getStatusBadge(o.order_status)}`}>
                            {o.order_status}
                          </span>
                        </td>
                        <td>
                          <div className="payment-method-box">
                             <span className="small text-uppercase fw-bold">{o.payment_method}</span>
                          </div>
                        </td>
                        <td className="text-end pe-4">
                          <Link
                            to={`/order-view/${o.order_uuid}`}
                            className="btn btn-icon-view"
                            title="Generate Invoice"
                          >
                            <FaFileInvoice className="me-1" /> View
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="8" className="text-center py-5">No active orders found in this range.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Bar */}
            <div className="card-footer bg-white py-3">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                <span className="text-muted small">
                  Showing <strong>{(page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, totalCount)}</strong> of {totalCount}
                </span>
                <nav>
                  <ul className="pagination pagination-sm mb-0 gap-1">
                    <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                      <button className="page-link rounded-2 border-0 bg-light px-3" onClick={() => setPage(page - 1)}>Prev</button>
                    </li>
                    <li className="page-item active"><span className="page-link rounded-2 border-0 px-3">{page}</span></li>
                    <li className={`page-item ${page >= Math.ceil(totalCount / limit) ? 'disabled' : ''}`}>
                      <button className="page-link rounded-2 border-0 bg-light px-3" onClick={() => setPage(page + 1)}>Next</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 10px; line-height: 1.2; }
        .order-table thead th { font-size: 11px; text-transform: uppercase; color: #8898aa; letter-spacing: 0.5px; padding: 15px 10px; }
        .order-table tbody td { padding: 12px 10px; border-bottom: 1px solid #f6f9fc; }
        
        /* Status Tags */
        .status-tag { padding: 4px 10px; border-radius: 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; display: inline-block; }
        .status-delivered { background: #dcfce7; color: #15803d; }
        .status-cancelled { background: #fee2e2; color: #b91c1c; }
        .status-shipped { background: #e0f2fe; color: #0369a1; }
        .status-pending { background: #fef3c7; color: #b45309; }
        .status-confirmed { background: #f3e8ff; color: #7e22ce; }

        /* Payment Pills */
        .payment-pill { font-size: 9px; text-transform: uppercase; font-weight: 800; }
        .payment-pill.paid { color: #10b981; }
        .payment-pill.unpaid { color: #ef4444; }

        .vendor-chip { background: #f1f5f9; padding: 2px 8px; border-radius: 4px; font-size: 11px; color: #475569; display: inline-flex; align-items: center; }
        .btn-icon-view { background: #f8fafc; border: 1px solid #e2e8f0; color: #6366f1; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: bold; transition: 0.2s; text-decoration: none; }
        .btn-icon-view:hover { background: #6366f1; color: #fff; }
        
        .payment-method-box { background: #fff; border: 1px solid #eee; padding: 2px 6px; border-radius: 4px; color: #666; font-size: 9px; display: inline-block; }
        .stat-pill { min-width: 140px; }
      `}</style>
    </>
  );
}

export default AllOrder;